/**
 * MonitoringCenter · /monitoreo · v2.0 (Fullscreen Map + Floating Panels)
 * ────────────────────────────────────────────────────────────────
 * War Room v2 · Neumorphic + Corporate palette
 *   - Fullscreen Leaflet map
 *   - Floating top-bar (branch filter + refresh + auto-refresh + stats compact)
 *   - Floating techs filter panel (left)
 *   - Floating solution-types table (right)
 *   - Floating pending-OTs badge (bottom-left)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Circle as LCircle, Polyline, Polygon, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Radar, Wifi, WifiOff, MapPin, Users, Loader2, RefreshCw, Clock, Route,
  AlertTriangle, Navigation as NavIcon, Timer, Zap, X, ChevronDown, ChevronUp, Wrench, Filter,
  ArrowLeft, LayoutGrid,
} from "lucide-react";

// ─── Marker icons ────────────────────────────────────────────────
const techIcon = (status) => {
  const color = status === "active" ? "#22C55E" : status === "stale" ? "#F59E0B" : "#94A3B8";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative"><div style="background:${color};width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>${status === "active" ? `<div style="position:absolute;top:-6px;left:-6px;width:36px;height:36px;border-radius:50%;background:${color}33;animation:pulse-ring 1.5s ease-out infinite"></div>` : ""}</div>`,
    iconSize: [24, 24], iconAnchor: [12, 12],
  });
};

const otIcon = (priority) => {
  const color = priority === "p1" ? "#DC2626" : priority === "p2" ? "#F59E0B" : "#2563EB";
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:14px;height:14px;border-radius:3px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);transform:rotate(45deg)"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });
};

const TRAIL_COLORS = ["#0EA5E9", "#8B5CF6", "#EC4899", "#F97316", "#10B981", "#EAB308", "#3B82F6", "#EF4444"];

const readPolygonCoords = (zone) => {
  const raw = zone?.geometry?.coords || zone?.geometry?.coordinates;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      if (Array.isArray(c) && c.length >= 2) return [Number(c[0]), Number(c[1])];
      if (c && typeof c === "object") return [Number(c.lat), Number(c.lng ?? c.lon)];
      return null;
    })
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
};

const readCircleCenter = (zone) => {
  const c = zone?.geometry?.center;
  if (!c) return null;
  if (Array.isArray(c) && c.length >= 2) return [Number(c[0]), Number(c[1])];
  if (typeof c === "object") return [Number(c.lat), Number(c.lng ?? c.lon)];
  return null;
};

function WarRoomAutoFit({ bounds, fallbackCenter, fallbackZoom }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    if (bounds && bounds.length > 1) {
      try {
        const latLngBounds = L.latLngBounds(bounds);
        if (latLngBounds.isValid()) {
          map.fitBounds(latLngBounds, { padding: [80, 80], maxZoom: 14 });
          return;
        }
      } catch { /* ignore */ }
    }
    if (bounds && bounds.length === 1) { map.setView(bounds[0], 12); return; }
    if (fallbackCenter) map.setView(fallbackCenter, fallbackZoom || 11);
  }, [JSON.stringify(bounds), JSON.stringify(fallbackCenter), fallbackZoom, map]);
  return null;
}

export default function MonitoringCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTech, setSelectedTech] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [zones, setZones] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");

  // Floating panel visibility
  const [techsPanelOpen, setTechsPanelOpen] = useState(true);
  const [solutionsPanelOpen, setSolutionsPanelOpen] = useState(true);
  const [techStatusFilter, setTechStatusFilter] = useState("all"); // all | active | stale | offline

  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/v1/monitoring/live");
      setData(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando datos");
      if (e?.response?.status !== 401) toast.error("No se pudo actualizar el monitoreo");
    } finally { setLoading(false); }
  };

  const loadGeo = async () => {
    try {
      const [z, b] = await Promise.all([
        api.get("/v1/config/zones").catch(() => ({ data: { items: [] } })),
        api.get("/v1/config/branches").catch(() => ({ data: { items: [] } })),
      ]);
      setZones(z.data?.items || z.data || []);
      setBranches(b.data?.items || b.data || []);
    } catch (e) { console.warn("Zones/branches load failed", e); }
  };

  useEffect(() => { load(); loadGeo(); }, []);
  useEffect(() => {
    if (!autoRefresh) return;
    timerRef.current = setInterval(load, 30_000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoRefresh]);

  const techs = data?.technicians || [];
  const pending = data?.pending_orders_no_tech || [];
  const stats = data?.stats || {};

  const visibleZones = useMemo(
    () => zones.filter((z) => z.active !== false).filter((z) => !selectedBranchId || z.branch_id === selectedBranchId),
    [zones, selectedBranchId]
  );

  // Filtered techs by status
  const filteredTechs = useMemo(
    () => techStatusFilter === "all" ? techs : techs.filter((t) => t.status === techStatusFilter),
    [techs, techStatusFilter]
  );

  // Solution types breakdown (from techs' orders)
  const solutionTypes = useMemo(() => {
    const map = new Map();
    techs.forEach((t) => {
      t.orders_today.forEach((o) => {
        const key = o.solution_type_name || o.solution_type_code || o.type || "Sin tipo";
        if (!map.has(key)) map.set(key, { name: key, count: 0, priorities: { p1: 0, p2: 0, p3: 0, p4: 0 } });
        const s = map.get(key);
        s.count++;
        const p = (o.priority || "p3").toLowerCase();
        s.priorities[p] = (s.priorities[p] || 0) + 1;
      });
    });
    pending.forEach((o) => {
      const key = o.solution_type_name || o.solution_type_code || o.type || "Sin tipo";
      if (!map.has(key)) map.set(key, { name: key, count: 0, priorities: { p1: 0, p2: 0, p3: 0, p4: 0 } });
      const s = map.get(key);
      s.count++;
      const p = (o.priority || "p3").toLowerCase();
      s.priorities[p] = (s.priorities[p] || 0) + 1;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [techs, pending]);

  const dynamicBounds = useMemo(() => {
    const pts = [];
    visibleZones.forEach((z) => {
      if (z.geometry_type === "polygon") readPolygonCoords(z).forEach((p) => pts.push(p));
      else if (z.geometry_type === "circle") { const c = readCircleCenter(z); if (c) pts.push(c); }
    });
    if (!selectedBranchId) {
      techs.forEach((t) => { if (t.last_ping?.lat && t.last_ping?.lon) pts.push([t.last_ping.lat, t.last_ping.lon]); });
    }
    return pts;
  }, [visibleZones, techs, selectedBranchId]);

  const mapCenter = useMemo(() => {
    const pts = techs.filter((t) => t.last_ping?.lat && t.last_ping?.lon);
    if (pts.length === 0) return [-0.1807, -78.4678];
    const lat = pts.reduce((a, t) => a + t.last_ping.lat, 0) / pts.length;
    const lon = pts.reduce((a, t) => a + t.last_ping.lon, 0) / pts.length;
    return [lat, lon];
  }, [techs]);

  return (
    <Layout hideChrome>
      <style>{`
        @keyframes pulse-ring { 0% { transform: scale(0.5); opacity: 1; } 100% { transform: scale(1.8); opacity: 0; } }
        .warroom-map .leaflet-container { background: #E4EAF0; }
      `}</style>
      <div className="warroom-container" style={{ position: "fixed", inset: 0, background: "var(--bg-app)" }} data-testid="warroom-page">
        {/* ═════ Fullscreen Map ═════ */}
        <div className="warroom-map" style={{ position: "absolute", inset: 0 }}>
          {loading && !data ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={40} /></div>
          ) : error ? (
            <div className="h-full flex items-center justify-center text-red-600 text-sm">{error}</div>
          ) : (
            <MapContainer center={mapCenter} zoom={11} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              <WarRoomAutoFit bounds={dynamicBounds} fallbackCenter={mapCenter} fallbackZoom={11} />
              {visibleZones.map((z) => {
                const branch = branches.find((b) => b.id === z.branch_id);
                const color = z.color || "#0891B2";
                if (z.geometry_type === "polygon") {
                  const positions = readPolygonCoords(z);
                  if (positions.length < 3) return null;
                  return (
                    <Polygon key={`zone-${z.id}`} positions={positions} pathOptions={{ color, weight: 2, fillColor: color, fillOpacity: 0.15 }}>
                      <Popup><div className="text-xs"><div className="font-bold" style={{ color }}>{z.name}</div><div className="text-gray-500 font-mono text-[10px]">{z.code}</div>{branch && <div className="text-gray-500 mt-1">Sucursal: <b>{branch.name}</b></div>}</div></Popup>
                    </Polygon>
                  );
                }
                if (z.geometry_type === "circle") {
                  const center = readCircleCenter(z);
                  if (!center) return null;
                  const radius = (z.geometry?.radius_km || 2.5) * 1000;
                  return (
                    <LCircle key={`zone-${z.id}`} center={center} radius={radius} pathOptions={{ color, weight: 2, fillColor: color, fillOpacity: 0.15 }}>
                      <Popup><div className="text-xs"><div className="font-bold" style={{ color }}>{z.name}</div><div className="text-gray-500 font-mono text-[10px]">{z.code}</div>{branch && <div className="text-gray-500 mt-1">Sucursal: <b>{branch.name}</b></div>}<div className="text-gray-500">Círculo · radio {z.geometry?.radius_km || 2.5} km</div></div></Popup>
                    </LCircle>
                  );
                }
                return null;
              })}
              {filteredTechs.map((t, i) => (t.breadcrumbs && t.breadcrumbs.length > 1 && (
                <Polyline key={`trail-${t.id}`} positions={t.breadcrumbs.map((p) => [p.lat, p.lon])} pathOptions={{ color: TRAIL_COLORS[i % TRAIL_COLORS.length], weight: selectedTech === t.id ? 4 : 2, opacity: selectedTech === t.id ? 0.9 : 0.5 }} />
              )))}
              {filteredTechs.map((t) => (t.next_order && t.last_ping?.lat && t.next_order.lat && (
                <Polyline key={`eta-${t.id}`} positions={[[t.last_ping.lat, t.last_ping.lon], [t.next_order.lat, t.next_order.lon]]} pathOptions={{ color: t.next_order.is_late ? "#DC2626" : "#E0E621", weight: selectedTech === t.id ? 5 : 3, opacity: selectedTech === t.id ? 0.9 : 0.7, dashArray: "6 8" }} />
              )))}
              {filteredTechs.map((t) => (t.last_ping?.lat && (
                <Marker key={`tech-${t.id}`} position={[t.last_ping.lat, t.last_ping.lon]} icon={techIcon(t.status)} eventHandlers={{ click: () => setSelectedTech(t.id) }}>
                  <Tooltip direction="top" offset={[0, -14]} permanent={selectedTech === t.id}><div className="text-[11px] font-semibold">{t.name}</div></Tooltip>
                  <Popup><div className="text-xs space-y-1 min-w-[180px]"><div className="font-bold text-[#0A3747]">{t.name}</div><div className="text-gray-600">{t.role_display || t.role}</div><div className="font-mono text-[10px] text-gray-500">Ping: hace {Math.round(t.last_ping.age_seconds / 60)} min</div><div className="text-[11px] pt-1 border-t">{t.orders_today.length} OT{t.orders_today.length === 1 ? "" : "s"} hoy</div></div></Popup>
                </Marker>
              )))}
              {filteredTechs.flatMap((t) => t.orders_today.filter((o) => o.lat && o.lon).map((o) => (
                <Marker key={`ot-${o.id}`} position={[o.lat, o.lon]} icon={otIcon(o.priority)}>
                  <Popup><div className="text-xs space-y-0.5 min-w-[180px]"><div className="font-bold font-mono">{o.code}</div><div className="text-gray-700">{o.client_name}</div><div className="text-gray-500 text-[11px]">{o.address}</div><div className="pt-1 border-t text-[10px] text-gray-600">Técnico: <b>{t.name}</b> · {o.priority?.toUpperCase()}</div></div></Popup>
                </Marker>
              )))}
              {pending.filter((o) => o.lat && o.lon).map((o) => (
                <CircleMarker key={`pend-${o.id}`} center={[o.lat, o.lon]} radius={6} pathOptions={{ color: "#E0E621", fillColor: "#E0E621", fillOpacity: 0.7, weight: 2 }}>
                  <Popup><div className="text-xs space-y-0.5 min-w-[180px]"><div className="font-bold font-mono">{o.code}</div><div className="text-gray-700">{o.client_name}</div><div className="text-red-600 font-semibold text-[11px]">Sin técnico asignado</div></div></Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* ═════ FLOATING TOPBAR — full-width with proper wrap ═════ */}
        <div
          className="absolute top-4 left-4 right-4 flex items-start justify-between gap-3 pointer-events-none z-[1000] flex-wrap"
        >
          {/* Left cluster: back + brand + branch */}
          <div className="flex items-center gap-2 pointer-events-auto flex-wrap">
            <button
              onClick={() => navigate("/apps")}
              className="neu-btn"
              style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700 }}
              title="Volver al menú principal"
              data-testid="warroom-back-btn"
            >
              <ArrowLeft size={14} /> Volver
            </button>
            <div className="neu-card px-4 py-2.5 flex items-center gap-2">
              <Radar size={20} style={{ color: "#0A3747" }} />
              <div className="leading-tight">
                <div className="text-[10px] uppercase font-semibold tracking-widest" style={{ color: "#6B7000" }}>War Room · M25</div>
                <div className="text-sm font-bold" style={{ color: "#0A3747" }}>Sala de Guerra</div>
              </div>
            </div>
            <div className="neu-card px-3 py-2 flex items-center gap-2">
              <MapPin size={14} style={{ color: "#0A3747" }} />
              <select
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                className="text-xs bg-transparent outline-none font-medium"
                style={{ color: "#0A3747", minWidth: 160 }}
                data-testid="warroom-branch-filter"
              >
                <option value="">Todas las sucursales · {zones.filter(z=>z.active!==false).length} zonas</option>
                {branches.map((b) => {
                  const zc = zones.filter((z) => z.active !== false && z.branch_id === b.id).length;
                  return <option key={b.id} value={b.id}>{b.name} · {zc} zona{zc !== 1 ? "s" : ""}</option>;
                })}
              </select>
            </div>
          </div>

          {/* Right cluster: compact stats + controls */}
          <div className="flex items-center gap-2 pointer-events-auto flex-wrap justify-end">
            <MiniStat icon={Users}    value={stats.total_techs || 0} label="Técnicos" bg="linear-gradient(145deg,#0C4051,#082E3D)" fg="#E0E621" />
            <MiniStat icon={Wifi}     value={stats.active || 0}      label="Activos"  bg="linear-gradient(145deg,#34D399,#10B981)" fg="#064E3B" />
            <MiniStat icon={Clock}    value={stats.stale || 0}       label="Stale"    bg="linear-gradient(145deg,#FCD34D,#F59E0B)" fg="#78350F" />
            <MiniStat icon={WifiOff}  value={stats.offline || 0}     label="Offline"  bg="linear-gradient(145deg,#CBD5E1,#94A3B8)" fg="#1E293B" />
            <MiniStat icon={Timer}    value={stats.techs_with_next_eta || 0} label="ETAs" bg="linear-gradient(145deg,#93C5FD,#3B82F6)" fg="#1E3A8A" />
            <MiniStat icon={AlertTriangle} value={stats.techs_running_late || 0} label="Tarde"
              bg={(stats.techs_running_late || 0) > 0 ? "linear-gradient(145deg,#F87171,#DC2626)" : "linear-gradient(145deg,#EDF34C,#C4CA12)"}
              fg={(stats.techs_running_late || 0) > 0 ? "#7F1D1D" : "#3F2E00"} />
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className="neu-btn"
              style={{ padding: "8px 12px", fontSize: 12 }}
              data-testid="toggle-autorefresh"
              title={autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
            >
              <div className={`w-2 h-2 rounded-full ${autoRefresh ? "animate-pulse" : ""}`} style={{ background: autoRefresh ? "#10B981" : "#94A3B8" }} />
              {autoRefresh ? "Live" : "Off"}
            </button>
            <button
              onClick={load}
              disabled={loading}
              className="neu-btn neu-btn-primary"
              style={{ padding: "8px 14px", fontSize: 12 }}
              data-testid="refresh-btn"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Refrescar
            </button>
          </div>
        </div>

        {/* ═════ FLOATING LEFT STACK · Techs + Solutions (evita superposición) ═════ */}
        <div
          className="absolute left-4 z-[1000] flex flex-col gap-3 pointer-events-none"
          style={{
            top: 108,                        // debajo del topbar
            bottom: 60,                       // sobre el badge/timestamp inferior
            width: 320,
            maxWidth: "calc(100vw - 32px)",
          }}
        >
          <FloatingTechsPanel
            open={techsPanelOpen}
            onToggle={() => setTechsPanelOpen(!techsPanelOpen)}
            techs={filteredTechs}
            selectedTech={selectedTech}
            onSelect={setSelectedTech}
            statusFilter={techStatusFilter}
            onStatusFilterChange={setTechStatusFilter}
            totalTechs={techs.length}
            stats={stats}
          />
          <FloatingSolutionsPanel
            open={solutionsPanelOpen}
            onToggle={() => setSolutionsPanelOpen(!solutionsPanelOpen)}
            items={solutionTypes}
          />
        </div>

        {/* ═════ FLOATING PENDING BADGE (BOTTOM-RIGHT) ═════ */}
        {pending.length > 0 && (
          <div className="absolute bottom-4 right-4 z-[1000]">
            <div
              className="neu-card px-3 py-2 flex items-center gap-2"
              style={{ background: "linear-gradient(145deg, #FDE68A, #FCD34D)" }}
              data-testid="pending-badge"
            >
              <AlertTriangle size={14} style={{ color: "#78350F" }} />
              <span className="text-xs font-bold" style={{ color: "#78350F" }}>{pending.length}</span>
              <span className="text-[11px] font-semibold" style={{ color: "#92400E" }}>
                OT{pending.length === 1 ? "" : "s"} sin asignar
              </span>
            </div>
          </div>
        )}

        {/* ═════ LAST UPDATE (BOTTOM-CENTER) ═════ */}
        {lastUpdate && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000]">
            <div className="neu-card px-3 py-1.5 text-[10px] font-mono" style={{ color: "#64748B" }}>
              Última actualización: {lastUpdate.toLocaleTimeString("es-EC")}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────
// MiniStat · colored KPI pill for topbar
// ─────────────────────────────────────────────────────────────────
function MiniStat({ icon: Icon, value, label, bg, fg }) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-2xl"
      style={{
        background: bg,
        boxShadow: "-4px -4px 10px rgba(255,255,255,0.75), 4px 4px 12px rgba(163,177,198,0.55), inset -1px -1px 2px rgba(0,0,0,0.05), inset 1px 1px 2px rgba(255,255,255,0.35)",
      }}
      title={label}
    >
      <Icon size={14} style={{ color: fg }} />
      <div className="leading-tight">
        <div className="text-xs font-bold" style={{ color: fg, textShadow: "0 1px 0 rgba(255,255,255,0.3)" }}>{value}</div>
        <div className="text-[9px] uppercase font-semibold tracking-wider" style={{ color: fg, opacity: 0.75 }}>{label}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FloatingTechsPanel · vertical stack item (left column)
// ─────────────────────────────────────────────────────────────────
function FloatingTechsPanel({ open, onToggle, techs, selectedTech, onSelect, statusFilter, onStatusFilterChange, totalTechs, stats }) {
  return (
    <div className="pointer-events-auto flex-shrink-0" style={{ maxHeight: open ? "58%" : "auto" }}>
      {open ? (
        <div
          className="neu-card afc-appear flex flex-col"
          style={{ width: "100%", maxHeight: "100%" }}
          data-testid="techs-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2">
              <Users size={16} style={{ color: "#0A3747" }} />
              <div className="text-sm font-bold" style={{ color: "#0A3747" }}>Técnicos en campo</div>
            </div>
            <button onClick={onToggle} className="neu-icon-btn" style={{ width: 28, height: 28 }} data-testid="techs-panel-close">
              <X size={12} />
            </button>
          </div>
          {/* Status filter pills */}
          <div className="px-3 py-2" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1.5" style={{ color: "#94A3B8" }}>Filtrar por estado</div>
            <div className="neu-pill-group w-full flex-wrap" style={{ padding: 3 }}>
              {[
                { k: "all",     label: "Todos",   count: totalTechs, color: "#0A3747" },
                { k: "active",  label: "Activos", count: stats.active || 0, color: "#10B981" },
                { k: "stale",   label: "Stale",   count: stats.stale || 0,  color: "#F59E0B" },
                { k: "offline", label: "Offline", count: stats.offline || 0, color: "#94A3B8" },
              ].map((s) => (
                <button
                  key={s.k}
                  onClick={() => onStatusFilterChange(s.k)}
                  className={`neu-pill ${statusFilter === s.k ? "active" : ""}`}
                  style={{ fontSize: 10.5, padding: "5px 8px", flex: 1 }}
                  data-testid={`tech-filter-${s.k}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                  {s.label} <span style={{ opacity: 0.6 }}>{s.count}</span>
                </button>
              ))}
            </div>
          </div>
          {/* List */}
          <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
            {techs.length === 0 && (
              <div className="p-6 text-center text-xs" style={{ color: "#94A3B8" }}>Sin técnicos que mostrar</div>
            )}
            {techs.map((t, i) => (
              <FloatingTechRow
                key={t.id}
                tech={t}
                color={TRAIL_COLORS[i % TRAIL_COLORS.length]}
                selected={selectedTech === t.id}
                onClick={() => onSelect(t.id === selectedTech ? null : t.id)}
              />
            ))}
          </div>
        </div>
      ) : (
        <button
          onClick={onToggle}
          className="neu-btn afc-appear self-start"
          style={{ padding: "10px 14px", fontSize: 12 }}
          data-testid="techs-panel-open"
        >
          <Users size={14} /> Técnicos <span className="ml-1 font-bold" style={{ color: "#0A3747" }}>{techs.length}</span>
        </button>
      )}
    </div>
  );
}

function FloatingTechRow({ tech, color, selected, onClick }) {
  const statusColor = tech.status === "active" ? "#10B981" : tech.status === "stale" ? "#F59E0B" : "#94A3B8";
  const ageMin = tech.last_ping ? Math.round(tech.last_ping.age_seconds / 60) : null;
  return (
    <div
      onClick={onClick}
      className="px-3 py-2.5 cursor-pointer transition"
      style={{
        background: selected ? "rgba(224,230,33,0.18)" : "transparent",
        borderBottom: "1px solid var(--border)",
        boxShadow: selected ? "inset 0 0 0 1.5px rgba(10,55,71,0.35)" : "none",
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "rgba(10,55,71,0.05)"; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
      data-testid={`tech-row-${tech.id}`}
    >
      <div className="flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: statusColor, animation: tech.status === "active" ? "pulse-ring 2s ease-out infinite" : "none" }} />
        <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ background: color }} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold truncate" style={{ color: "#0A3747" }}>{tech.name}</div>
          <div className="text-[10px]" style={{ color: "#64748B" }}>
            {tech.status === "active" && ageMin !== null && `hace ${ageMin} min · `}
            {tech.status === "stale" && `stale (${ageMin} min) · `}
            {tech.status === "offline" && "sin GPS · "}
            {tech.orders_today.length} OT{tech.orders_today.length === 1 ? "" : "s"}
          </div>
        </div>
        {tech.orders_today.length > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#0A3747", color: "#E0E621" }}>
            {tech.orders_today.length}
          </span>
        )}
      </div>
      {selected && tech.next_order && (
        <div className="mt-2 pt-2 px-2" style={{ borderTop: "1px dashed var(--border)" }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#6B7000" }}>Próxima OT</div>
          <div className="text-[11px] font-mono font-semibold" style={{ color: "#0A3747" }}>{tech.next_order.code}</div>
          <div className="text-[10px]" style={{ color: "#64748B" }}>{tech.next_order.client_name || "—"}</div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] font-mono flex items-center gap-1" style={{ color: "#0A3747" }}>
              <NavIcon size={10} /> {tech.next_order.distance_km} km
            </span>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{
                background: tech.next_order.is_late ? "linear-gradient(145deg,#F87171,#DC2626)" : "linear-gradient(145deg,#EDF34C,#C4CA12)",
                color: tech.next_order.is_late ? "#FFFFFF" : "#3F2E00",
              }}
            >
              <Timer size={9} className="inline mr-0.5" />
              {tech.next_order.eta_min < 1 ? "<1" : Math.round(tech.next_order.eta_min)} min
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FloatingSolutionsPanel · vertical stack item (left column)
// ─────────────────────────────────────────────────────────────────
function FloatingSolutionsPanel({ open, onToggle, items }) {
  return (
    <div className="pointer-events-auto flex-1 min-h-0" style={{ maxHeight: open ? "100%" : "auto" }}>
      {open ? (
        <div
          className="neu-card afc-appear flex flex-col"
          style={{ width: "100%", maxHeight: "100%" }}
          data-testid="solutions-panel"
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2">
              <Wrench size={16} style={{ color: "#0A3747" }} />
              <div className="text-sm font-bold" style={{ color: "#0A3747" }}>Tipos de Solución</div>
            </div>
            <button onClick={onToggle} className="neu-icon-btn" style={{ width: 28, height: 28 }} data-testid="solutions-panel-close">
              <X size={12} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2" style={{ minHeight: 0 }}>
            {items.length === 0 ? (
              <div className="p-6 text-center text-xs" style={{ color: "#94A3B8" }}>Sin OTs para agrupar</div>
            ) : (
              <table className="w-full text-xs" data-testid="solutions-table">
                <thead>
                  <tr>
                    <th className="text-left px-2 py-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: "#6B7000" }}>Tipo</th>
                    <th className="text-right px-2 py-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: "#6B7000" }}>Total</th>
                    <th className="text-right px-2 py-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: "#6B7000" }}>Prio</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.name} className="transition" style={{ borderTop: "1px solid var(--border)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(10,55,71,0.04)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td className="px-2 py-2 font-medium" style={{ color: "#0A3747" }}>{it.name}</td>
                      <td className="px-2 py-2 text-right">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full font-bold text-[11px]"
                          style={{
                            background: "linear-gradient(145deg,#0C4051,#082E3D)",
                            color: "#E0E621",
                            boxShadow: "inset -1px -1px 2px rgba(255,255,255,0.15), inset 1px 1px 2px rgba(0,0,0,0.3)",
                          }}
                        >
                          {it.count}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {it.priorities.p1 > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEE2E2", color: "#DC2626" }}>P1·{it.priorities.p1}</span>}
                          {it.priorities.p2 > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#D97706" }}>P2·{it.priorities.p2}</span>}
                          {it.priorities.p3 > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#DBEAFE", color: "#2563EB" }}>P3·{it.priorities.p3}</span>}
                          {it.priorities.p4 > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#64748B" }}>P4·{it.priorities.p4}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        <button
          onClick={onToggle}
          className="neu-btn afc-appear self-start"
          style={{ padding: "10px 14px", fontSize: 12 }}
          data-testid="solutions-panel-open"
        >
          <Wrench size={14} /> Tipos <span className="ml-1 font-bold" style={{ color: "#0A3747" }}>{items.length}</span>
        </button>
      )}
    </div>
  );
}
